import { useEffect, useRef, useState, type RefObject } from "react";
import { BASKETBALL_SWEET_WINDOW, SOCCER_AIM_MAX_RAD, basketballSweetSpot, type SportsAction } from "@townsy/contracts";
import { isReservedKey, isTypingTarget } from "../../input/focusGuard";
import { chargePower, useAim, useSportsAim } from "./aimStore";
import { SPORTS_KEY_CODES, tennisAimLabel, type SportsHudButton, type SportsHudView } from "./hudModel";

/** Power sent for a charged button pressed with the keyboard's Enter / Space on the focused button (no hold). */
const KEYBOARD_CLICK_POWER = { shoot: 0.6, kick: 0.75 } as const;
const AIM_KEYS: Record<string, -1 | 1> = { KeyA: -1, KeyD: 1, ArrowLeft: -1, ArrowRight: 1 };

export interface SportsControls {
  /** A hidden focus target: focused while a charge is held, so the town's walking pauses (A / D aim instead). */
  blockerRef: RefObject<HTMLSpanElement | null>;
  begin(b: SportsHudButton): void;
  end(b: SportsHudButton): void;
  fire(b: SportsHudButton): void;
  /** A charged button activated without a hold (keyboard Enter / Space on the focused button). */
  quick(b: SportsHudButton): void;
}

/**
 * The sports center's controls, shared by the full overlay and the compact strip (one of them is mounted at a time):
 * F / G / R press the zone's buttons; a charged button (Shoot, Kick) charges while held and sends the power on release;
 * while a kick charges, A / D (or the arrows) nudge the aim. The browser only sends intents; the server decides.
 */
export function useSportsControls(view: SportsHudView, send: (action: Record<string, unknown>) => boolean): SportsControls {
  const blockerRef = useRef<HTMLSpanElement | null>(null);
  const viewRef = useRef(view);
  viewRef.current = view;
  const sendRef = useRef(send);
  sendRef.current = send;

  const blockWalking = (on: boolean) => {
    const el = blockerRef.current;
    if (!el) return;
    if (on) el.focus({ preventScroll: true });
    else if (document.activeElement === el) el.blur();
  };
  const fire = (b: SportsHudButton) => {
    if (b.disabledReason) return;
    const action: Record<string, unknown> = { ...b.action };
    if (b.aimed) action["aim"] = useSportsAim.getState().tennisAim;
    sendRef.current(action);
  };
  const sendCharged = (kind: "shoot" | "kick", power: number) => {
    const p = Math.round(power * 1000) / 1000;
    if (kind === "shoot") sendRef.current({ type: "shoot", power: p } satisfies SportsAction);
    else {
      const s = useSportsAim.getState();
      const yaw = s.facing === null ? undefined : Math.round((s.facing + s.aimOffset) * 1000) / 1000;
      sendRef.current({ type: "kick", power: p, ...(yaw === undefined ? {} : { yaw }) } satisfies SportsAction);
      s.resetAim();
    }
  };
  const controls: SportsControls = {
    blockerRef,
    begin(b) {
      if (b.disabledReason) return;
      if (!b.charge) {
        fire(b);
        return;
      }
      useSportsAim.getState().startCharge(b.charge, performance.now());
      blockWalking(true);
    },
    end(b) {
      if (!b.charge) return;
      blockWalking(false);
      const r = useSportsAim.getState().endCharge(performance.now());
      if (r && !b.disabledReason) sendCharged(r.kind, r.power);
    },
    fire,
    quick(b) {
      if (b.disabledReason || !b.charge) return;
      const dist = useSportsAim.getState().hoopDistance;
      sendCharged(b.charge, b.charge === "shoot" && dist !== null ? basketballSweetSpot(dist) - 0.12 : KEYBOARD_CLICK_POWER[b.charge]);
    },
  };
  const controlsRef = useRef(controls);
  controlsRef.current = controls;

  useEffect(() => {
    const ours = (t: EventTarget | null) => t !== null && t === blockerRef.current;
    // Shift is allowed: it only means "run" (ADR-0018), and a tennis player must be able to run to the ball and swing.
    const blocked = (ev: KeyboardEvent) => {
      if (isReservedKey(ev)) return true;
      const typing = (!ours(ev.target) && isTypingTarget(ev.target as Element | null)) || (!ours(document.activeElement) && isTypingTarget(document.activeElement));
      return typing || Boolean(document.querySelector('[role="dialog"]'));
    };
    const onDown = (ev: KeyboardEvent) => {
      const aim = AIM_KEYS[ev.code];
      if (aim !== undefined && useSportsAim.getState().charging?.kind === "kick") {
        ev.preventDefault();
        useSportsAim.getState().nudgeAim(aim);
        return;
      }
      const key = SPORTS_KEY_CODES[ev.code];
      if (!key || ev.repeat || blocked(ev)) return;
      const b = viewRef.current.buttons.find((x) => x.key === key);
      if (!b) return;
      ev.preventDefault();
      controlsRef.current.begin(b);
    };
    const onUp = (ev: KeyboardEvent) => {
      const key = SPORTS_KEY_CODES[ev.code];
      if (!key) return;
      const b = viewRef.current.buttons.find((x) => x.key === key);
      if (b?.charge && useSportsAim.getState().charging) controlsRef.current.end(b);
    };
    const onBlur = () => {
      useSportsAim.getState().cancelCharge();
    };
    window.addEventListener("keydown", onDown);
    window.addEventListener("keyup", onUp);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("keydown", onDown);
      window.removeEventListener("keyup", onUp);
      window.removeEventListener("blur", onBlur);
      useSportsAim.getState().cancelCharge();
    };
  }, []);

  return controls;
}

/** The zone's action buttons: charged ones work by press-and-hold (mouse, touch or the key), the rest by a press. */
export function SportsButtons({ view, controls, compact = false }: { view: SportsHudView; controls: SportsControls; compact?: boolean }) {
  const cue = useAim((s) => s.swingCue);
  return (
    <div className={`sports-hud-actions${compact ? " compact" : ""}`} role="group" aria-label={`${view.title} actions`}>
      {view.buttons.map((b) => {
        const ready = b.aimed && cue === "ready" && !b.disabledReason;
        return (
          <button
            key={b.id}
            type="button"
            className={`btn${b.primary ? " btn-primary" : ""} sports-hud-btn${ready ? " ready" : ""}`}
            data-testid={`sports-${b.id}`}
            aria-keyshortcuts={b.key}
            aria-disabled={b.disabledReason ? "true" : undefined}
            title={b.disabledReason ?? (b.charge ? `${b.label}: hold, then let go (key ${b.key})` : `${b.label} (key ${b.key})`)}
            onPointerDown={
              b.charge
                ? (e) => {
                    e.preventDefault();
                    e.currentTarget.setPointerCapture?.(e.pointerId);
                    controls.begin(b);
                  }
                : undefined
            }
            onPointerUp={b.charge ? () => controls.end(b) : undefined}
            onPointerCancel={b.charge ? () => controls.end(b) : undefined}
            onClick={(e) => {
              if (b.charge) {
                // Pointer presses are handled above; Enter / Space on the focused button send a standard one.
                if (e.detail === 0) controls.quick(b);
                return;
              }
              controls.fire(b);
            }}
          >
            <span>{b.label}</span>
            <kbd aria-hidden="true">{b.key}</kbd>
          </button>
        );
      })}
    </div>
  );
}

/** Live power while charging (re-renders every animation frame only during a charge). */
function useChargePower(): { kind: "shoot" | "kick" | null; power: number } {
  const charging = useAim((s) => s.charging);
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!charging) return;
    let raf = 0;
    const loop = () => {
      setTick((t) => t + 1);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [charging]);
  return { kind: charging?.kind ?? null, power: charging ? chargePower(charging.startedAt, performance.now()) : 0 };
}

/** The power meter: the fill while charging; for basketball the green sweet band for your distance to the hoop. */
export function PowerMeter({ kind }: { kind: "shoot" | "kick" }) {
  const live = useChargePower();
  const dist = useAim((s) => s.hoopDistance);
  const power = live.kind === kind ? live.power : 0;
  const sweet = kind === "shoot" && dist !== null ? basketballSweetSpot(dist) : null;
  const inBand = sweet !== null && live.kind === kind && Math.abs(power - sweet) <= BASKETBALL_SWEET_WINDOW;
  const pct = (v: number) => `${Math.round(Math.max(0, Math.min(1, v)) * 1000) / 10}%`;
  return (
    <div className="sports-meter" data-testid="sports-meter" data-kind={kind}>
      <div
        className={`sports-meter-track${inBand ? " in-band" : ""}`}
        role="meter"
        aria-label={kind === "shoot" ? "Shot power" : "Kick power"}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(power * 100)}
      >
        <span className="sports-meter-fill" style={{ width: pct(power) }} />
        {sweet !== null ? (
          <span
            className="sports-meter-sweet"
            data-testid="sports-meter-sweet"
            style={{ left: pct(sweet - BASKETBALL_SWEET_WINDOW), width: pct(BASKETBALL_SWEET_WINDOW * 2) }}
          />
        ) : null}
      </div>
      <span className="sports-meter-label small">
        {kind === "shoot"
          ? dist !== null
            ? `${dist.toFixed(1)} m to the hoop: let go in the green`
            : "Hold, then let go in the green"
          : live.kind === "kick"
            ? `Power ${Math.round(power * 100)}%`
            : "Hold to power up"}
      </span>
    </div>
  );
}

/** Soccer aim: ◀ ▶ (also A / D while the kick charges), shown as degrees left / right of the way you face. */
export function AimControl() {
  const offset = useAim((s) => s.aimOffset);
  const nudge = useAim((s) => s.nudgeAim);
  const deg = Math.round((offset * 180) / Math.PI);
  const label = deg === 0 ? "straight" : `${Math.abs(deg)}° ${deg < 0 ? "left" : "right"}`;
  const max = Math.round((SOCCER_AIM_MAX_RAD * 180) / Math.PI);
  return (
    <div className="sports-aim" role="group" aria-label="Kick aim">
      <button type="button" className="btn sports-aim-btn" data-testid="sports-aim-left" aria-label="Aim left" onClick={() => nudge(-1)} disabled={deg <= -max}>
        {"◀︎"}
      </button>
      <span className="sports-aim-label" data-testid="sports-aim-label">
        Aim {label}
      </span>
      <button type="button" className="btn sports-aim-btn" data-testid="sports-aim-right" aria-label="Aim right" onClick={() => nudge(1)} disabled={deg >= max}>
        {"▶︎"}
      </button>
    </div>
  );
}

/** Tennis: where a swing now would send the ball, from your point of view. */
export function TennisAimLine({ side }: { side: number | null }) {
  const aim = useAim((s) => s.tennisAim);
  const cue = useAim((s) => s.swingCue);
  return (
    <p className="sports-tennis-aim small" data-testid="sports-tennis-aim" data-cue={cue}>
      {cue === "ready" ? <strong className="sports-ready">Swing now! </strong> : null}
      Aim: <strong>{tennisAimLabel(aim, side)}</strong> (hold A or D as you swing)
    </p>
  );
}

const HELP_KEY = "townsy.sports.tennisHelpSeen";
/** The tennis how-to line, until the visitor dismisses it (remembered in this browser when storage allows). */
export function TennisHelp({ text }: { text: string }) {
  const [seen, setSeen] = useState(() => {
    try {
      return typeof localStorage !== "undefined" && localStorage.getItem(HELP_KEY) === "1";
    } catch {
      return false;
    }
  });
  if (seen) return null;
  return (
    <p className="sports-help small" data-testid="sports-tennis-help">
      <span>{text}</span>
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        onClick={() => {
          setSeen(true);
          try {
            localStorage.setItem(HELP_KEY, "1");
          } catch {
            // private mode: it simply shows again next visit
          }
        }}
      >
        Got it
      </button>
    </p>
  );
}

/** The hidden element focused while charging (walking pauses; A / D aim). */
export function WalkPause({ controls }: { controls: SportsControls }) {
  return <span ref={controls.blockerRef} tabIndex={-1} data-movement-block="true" className="visually-hidden" aria-hidden="true" />;
}
